import { useState, useEffect } from 'react';
import { notifyActiveEventsUpdated } from '../../../../utils/activeEventsBus';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  FormControl,
  Divider,
  Alert,
} from '@mui/material';
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";
import { useLiveLocation } from "../../../common/useLiveLocation";
import CircularProgress from "@mui/material/CircularProgress";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Submitting a felt report now records who submitted it, so it requires a
// logged-in session - the same JWT every other authenticated call in this
// app already attaches.
const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
    {text}
  </Typography>
);

const inputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '15px',
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#2563eb' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: '15px', '&.Mui-focused': { color: '#2563eb' } },
};

const radioSx = { color: '#94a3b8', '&.Mui-checked': { color: '#2563eb' } };

const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
};

// Pans the map to a geolocated position - only fires on the initial
// geolocation fix (via the `target` prop's changing `ts`), never on a plain
// map click/drag, so manually placing/adjusting the marker never yanks the
// view around. Matches the hazard Felt Report forms' PanToLatLng pattern.
const PanToLatLng = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lon], 17);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.ts]);
  return null;
};

const initialFormData = {
  felt_it: '',
  municipality: '',
  sub_municipality: '',
  suco: '',
  situation: '',
  building_structure: '',
  ground_shaking: '',
  location: '',
  latitude: '',
  longitude: '',
  date_time: '',
  message: '',
  people_injured_count: '',
  people_deaths_count: '',
  houses_affected_count: '',
  livestock_lost_count: '',
  estimated_loss_amount: '',
};

function FeltReport() {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [municipalities, setMunicipalities] = useState([]);
  const [subMunicipalities, setSubMunicipalities] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [sucos, setSucos] = useState([]);
  const [selectedSubMunicipality, setSelectedSubMunicipality] = useState('');

  // Browser geolocation + adm3-boundary reverse lookup, shared with every
  // other felt-report form.
  const { locating, locationError, setLocationError, requestBrowserLocation, resolveAdmFromLatLng, resolveLandmark } = useLiveLocation();
  const [geoFlyTarget, setGeoFlyTarget] = useState(null);
  const [landmarkLoading, setLandmarkLoading] = useState(false);
  const [landmarkNotFound, setLandmarkNotFound] = useState(false);

  // Fills Municipality/Sub-municipality/Suco from a resolved adm hierarchy -
  // shared by both the initial geolocation fix and every later manual
  // click/drag of the marker, so the two paths can't drift apart.
  const applyResolvedAdm = (adm) => {
    setSelectedMunicipality(adm.adm1_code);
    setSelectedSubMunicipality(adm.adm2_code);
    setFormData(prev => ({ ...prev, municipality: adm.adm1_code, sub_municipality: adm.adm2_code, suco: adm.adm3_code }));
  };

  const handlePointMoved = async (lat, lon) => {
    const adm = await resolveAdmFromLatLng(lat, lon);
    if (adm) { applyResolvedAdm(adm); setLocationError(""); }
    else setLocationError("This point doesn't fall within a known suco boundary - select Municipality/Sub-municipality/Suco manually below.");

    setLandmarkLoading(true);
    setLandmarkNotFound(false);
    const landmark = await resolveLandmark(lat, lon);
    setLandmarkLoading(false);
    if (landmark) setFormData(prev => ({ ...prev, location: landmark }));
    else { setFormData(prev => ({ ...prev, location: '' })); setLandmarkNotFound(true); }
  };

  const handleUseMyLocation = async () => {
    try {
      const { lat, lon } = await requestBrowserLocation();
      setMarkerPosition({ lat, lng: lon });
      setGeoFlyTarget({ lat, lon, ts: Date.now() });
      setFormData(prev => ({ ...prev, latitude: lat.toFixed(6), longitude: lon.toFixed(6) }));
      await handlePointMoved(lat, lon);
    } catch (err) {
      // requestBrowserLocation already recorded a user-facing locationError
    }
  };

  // Prompts for location as soon as the form opens - no button to click.
  // If denied/unsupported, locationError guides the user to the manual
  // dropdowns below, which always still work.
  useEffect(() => {
    handleUseMyLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios.post(`${backendUrl}Municipalities/municipalities_list/`)
      .then(r => setMunicipalities(r.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedMunicipality) {
      axios.post(`${backendUrl}Municipalities/sub_municipalities_list/`, { adm2_code: selectedMunicipality })
        .then(r => setSubMunicipalities(r.data))
        .catch(console.error);
    } else {
      setSubMunicipalities([]);
      setSucos([]);
    }
  }, [selectedMunicipality]);

  useEffect(() => {
    if (selectedSubMunicipality) {
      axios.post(`${backendUrl}Municipalities/suco_list/`, { adm2_code: selectedSubMunicipality })
        .then(r => setSucos(r.data))
        .catch(console.error);
    } else {
      setSucos([]);
    }
  }, [selectedSubMunicipality]);

  const handleMunicipalityChange = (e) => {
    setSelectedMunicipality(e.target.value);
    setSelectedSubMunicipality('');
    setFormData(prev => ({ ...prev, municipality: e.target.value, sub_municipality: '', suco: '' }));
  };

  const handleSubMunicipalityChange = (e) => {
    setSelectedSubMunicipality(e.target.value);
    setFormData(prev => ({ ...prev, sub_municipality: e.target.value, suco: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMarkerMoved = (latlng) => {
    setMarkerPosition(latlng);
    setFormData(prev => ({ ...prev, latitude: latlng.lat.toFixed(6), longitude: latlng.lng.toFixed(6) }));
    handlePointMoved(latlng.lat, latlng.lng);
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setMarkerPosition(null);
    setGeoFlyTarget(null);
    setSelectedMunicipality('');
    setSelectedSubMunicipality('');
    setSucos([]);
    setSubmitSuccess(false);
    setSubmitError('');
    setLocationError('');
    setLandmarkNotFound(false);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);
    if (!formData.message || !formData.message.trim()) {
      setSubmitError('Description is required.');
      return;
    }
    const payload = new FormData();
    Object.entries(formData).forEach(([k, v]) => { if (v !== '') payload.append(k, v); });

    try {
      const response = await axios.post(`${backendUrl}/api/submit_eq_felt_report/`, payload, {
        headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() },
      });
      if (response.status === 201 || response.status === 200) {
        setSubmitSuccess(true);
        notifyActiveEventsUpdated();
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError('Failed to submit report. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      const msg = error.response?.data?.detail || JSON.stringify(error.response?.data);
      setSubmitError(`An error occurred while submitting the report: ${msg}`);
    }
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>

      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
          Earthquake Felt Report
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.25 }}>
          Tell us about your earthquake experience
        </Typography>
      </Box>

      {/* Form Body */}
      <Box component="form" onSubmit={handleReportSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

        {submitSuccess && (
          <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>
            Report submitted successfully! Form will reset in 2 seconds.
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>
        )}

        {/* Felt it? */}
        <Box>
          <SectionLabel text="Did You Feel It?" />
          <FormControl>
            <FormLabel sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 0.5 }}>
              Did you feel the earthquake?
            </FormLabel>
            <RadioGroup row value={formData.felt_it} onChange={handleChange} name="felt_it">
              <FormControlLabel value="yes" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>Yes</Typography>} sx={{ mr: 3 }} />
              <FormControlLabel value="no" control={<Radio size="small" sx={radioSx} />} label={<Typography sx={{ fontSize: '15px', color: '#374151' }}>No</Typography>} />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* Location: details on the left, map on the right */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
          {/* LEFT: Location Information */}
          <Box>
            <SectionLabel text="Location Information" />

            {locating && (
              <Typography sx={{ fontSize: '13px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={14} /> Requesting your location…
              </Typography>
            )}

            {locationError && !(selectedMunicipality && selectedSubMunicipality && formData.suco) && (
              <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>{locationError}</Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2.5 }}>
              <TextField label="Municipality" select size="small" fullWidth sx={inputSx} value={selectedMunicipality} onChange={handleMunicipalityChange} name="municipality">
                <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a municipality</em></MenuItem>
                {municipalities.map((m, i) => <MenuItem key={i} value={m.adm1_code}>{m.adm1}</MenuItem>)}
              </TextField>
              <TextField label="Sub Municipality" select size="small" fullWidth sx={inputSx} value={formData.sub_municipality} onChange={handleSubMunicipalityChange} name="sub_municipality" disabled={!selectedMunicipality}>
                <MenuItem value=""><em style={{ color: '#94a3b8' }}>{selectedMunicipality ? 'Select a sub-municipality' : 'Select a municipality first'}</em></MenuItem>
                {subMunicipalities.map((s, i) => <MenuItem key={i} value={s.adm2_code}>{s.adm2}</MenuItem>)}
              </TextField>
              <TextField label="Suco" select size="small" fullWidth sx={inputSx} value={formData.suco} onChange={handleChange} name="suco" disabled={!selectedSubMunicipality}>
                <MenuItem value=""><em style={{ color: '#94a3b8' }}>{selectedSubMunicipality ? 'Select a suco' : 'Select a sub-municipality first'}</em></MenuItem>
                {sucos.map((s, i) => <MenuItem key={i} value={s.adm3_code}>{s.adm3}</MenuItem>)}
              </TextField>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <TextField
                label="Nearest landmark/location"
                size="small"
                fullWidth
                sx={inputSx}
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder={landmarkLoading ? 'Looking up the nearest landmark…' : 'Enter location details'}
              />
              {landmarkNotFound && !formData.location && (
                <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>No landmark found nearby — add one.</Typography>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
              <TextField
                label="Latitude"
                size="small"
                fullWidth
                sx={inputSx}
                value={formData.latitude}
                InputProps={{ readOnly: true }}
                placeholder="Click map to set"
              />
              <TextField
                label="Longitude"
                size="small"
                fullWidth
                sx={inputSx}
                value={formData.longitude}
                InputProps={{ readOnly: true }}
                placeholder="Click map to set"
              />
            </Box>
          </Box>

          {/* RIGHT: Map */}
          <Box>
            <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <MapContainer center={[-8.7747, 125.7275]} zoom={9} maxZoom={22} style={{ width: '100%', height: '450px' }}>
                <MapHomeControl />
                <MapInfoControl text="Click on the map (or drag the pin) to mark the location - Municipality/Sub-municipality/Suco update automatically." />
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={22} maxNativeZoom={19} />
                <PanToLatLng target={geoFlyTarget} />
                <MapClickHandler onMapClick={handleMarkerMoved} />
                {markerPosition && (
                  <Marker
                    position={markerPosition}
                    draggable={true}
                    eventHandlers={{ dragend: (e) => handleMarkerMoved(e.target.getLatLng()) }}
                  >
                    <Popup>
                      Lat: {markerPosition.lat.toFixed(6)}<br />
                      Lng: {markerPosition.lng.toFixed(6)}<br />
                      <em>Drag to adjust — updates automatically.</em>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* Earthquake Details */}
        <Box>
          <SectionLabel text="Earthquake Details" />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <TextField label="What was your situation during the earthquake?" select size="small" fullWidth sx={inputSx} name="situation" value={formData.situation} onChange={handleChange}>
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a situation</em></MenuItem>
              <MenuItem value="In Moving Vehicle">In Moving Vehicle</MenuItem>
              <MenuItem value="In Stopped Vehicle">In Stopped Vehicle</MenuItem>
              <MenuItem value="Outside">Outside</MenuItem>
              <MenuItem value="Inside">Inside</MenuItem>
            </TextField>

            <TextField label="If inside, type of building or structure" select size="small" fullWidth sx={inputSx} name="building_structure" value={formData.building_structure} onChange={handleChange}>
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select a building type</em></MenuItem>
              <MenuItem value="Mobile home /Permanent Foundation">Mobile home / Permanent Foundation</MenuItem>
              <MenuItem value="Office building /School">Office building / School</MenuItem>
              <MenuItem value="Apartment building">Apartment building</MenuItem>
              <MenuItem value="Single family Home or duplex">Single family home or duplex</MenuItem>
            </TextField>

            <TextField label="How would you describe the ground shaking?" select size="small" fullWidth sx={inputSx} name="ground_shaking" value={formData.ground_shaking} onChange={handleChange}>
              <MenuItem value=""><em style={{ color: '#94a3b8' }}>Select an intensity</em></MenuItem>
              <MenuItem value="Not Felt">Not Felt</MenuItem>
              <MenuItem value="Weak">Weak</MenuItem>
              <MenuItem value="Mild">Mild</MenuItem>
              <MenuItem value="Moderate">Moderate</MenuItem>
              <MenuItem value="Strong">Strong</MenuItem>
              <MenuItem value="Violent">Violent</MenuItem>
            </TextField>

            <TextField
              label="Date and Time"
              type="datetime-local"
              size="small"
              fullWidth
              sx={inputSx}
              name="date_time"
              value={formData.date_time}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* Losses & Damages */}
        <Box>
          <SectionLabel text="Losses & Damages" />
          <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1.5 }}>Approximate figures are fine — leave blank if unknown.</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 2.5 }}>
            <TextField label="People injured (approx.)" type="number" size="small" fullWidth sx={inputSx} name="people_injured_count" value={formData.people_injured_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Deaths reported (approx.)" type="number" size="small" fullWidth sx={inputSx} name="people_deaths_count" value={formData.people_deaths_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Houses/structures affected (approx.)" type="number" size="small" fullWidth sx={inputSx} name="houses_affected_count" value={formData.houses_affected_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Livestock lost (approx.)" type="number" size="small" fullWidth sx={inputSx} name="livestock_lost_count" value={formData.livestock_lost_count} onChange={handleChange} inputProps={{ min: 0 }} />
            <TextField label="Estimated monetary loss (USD)" type="number" size="small" fullWidth sx={inputSx} name="estimated_loss_amount" value={formData.estimated_loss_amount} onChange={handleChange} inputProps={{ min: 0, step: '0.01' }} />
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#e2e8f0' }} />

        {/* Description */}
        <Box>
          <SectionLabel text="Description" />
          <TextField
            label="Describe what happened"
            multiline
            rows={4}
            fullWidth
            required
            sx={inputSx}
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Provide a description of this report"
          />
        </Box>

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleReset}
            sx={{ borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: '#64748b', background: '#f8fafc' }, px: 4, py: 1, borderRadius: '8px', textTransform: 'none', fontSize: '14px', fontWeight: 600 }}
          >
            Reset
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ background: '#2563eb', '&:hover': { background: '#1d4ed8' }, px: 4, py: 1, borderRadius: '8px', textTransform: 'none', fontSize: '14px', fontWeight: 600, boxShadow: 'none' }}
          >
            Submit Report
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default FeltReport;
